import './participantSelector.scss';

import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ButtonColor, Collapse, Size } from '@eliberty/design-system-react';

import type { DisplayMode } from '~atoms';
import { DISPLAY_MODE, SelectorHeader, SelectorLayout } from '~atoms';
import { SELECTOR_MODE } from '~constants';
import { getTotalPriceWithOptions } from '~helpers';
import { useSelectorCollapse } from '~hooks';
import {
  ParticipantAgeModal,
  ParticipantConsumerCategorySelector,
  ParticipantLine,
} from '~molecules';
import { ConsumerCategory, OrderItem } from '~types/appsync.types';
import { ConsumerCategoryConfirmationState, Participant } from '~types/participant';
import { getMinMaxAges } from '~utils';

const buildCountsFromParticipants = (participantsList: Participant[] = []) =>
  participantsList.reduce(
    (acc, participant) => {
      const categoryKey = participant.consumerCategory.shortname;
      acc[categoryKey] = (acc[categoryKey] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

interface ParticipantSelectorProps {
  participants?: Participant[];
  orderItems?: OrderItem[];
  currency?: string;
  isSimulationOnError?: boolean;
  consumerCategories: ConsumerCategory[];
  participantsLimit?: number;
  participantMode?: SELECTOR_MODE;
  isLoading?: boolean;
  disabled?: boolean;
  onChangeParticipants: (participants: Participant[]) => void;
  onConsumerCategoryConfirmationChange?: (state: ConsumerCategoryConfirmationState | null) => void;
  displayMode?: DisplayMode;
  forceCollapsed?: boolean;
  onPopoverToggle?: () => void;
  onClickOutside?: () => void;
}

export const ANIMATION_PARTICIPANT_DURATION = 250;

const ParticipantSelector = ({
  participants,
  orderItems = [],
  currency,
  isSimulationOnError = false,
  consumerCategories,
  participantsLimit,
  participantMode = SELECTOR_MODE.AGE,
  isLoading = false,
  disabled = false,
  onChangeParticipants,
  onConsumerCategoryConfirmationChange,
  displayMode = DISPLAY_MODE.COLLAPSE,
  forceCollapsed,
  onPopoverToggle,
  onClickOutside,
}: ParticipantSelectorProps) => {
  const intl = useIntl();
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>(
    participants ?? [],
  );
  const [consumerCategoryCounts, setConsumerCategoryCounts] = useState<Record<string, number>>(() =>
    buildCountsFromParticipants(participants ?? []),
  );
  const [isOpenModalParticipant, setIsOpenModalParticipant] = useState(false);
  const handleCloseModalParticipant = () => setIsOpenModalParticipant(false);
  const { isCollapsed, setIsCollapsed, toggle } = useSelectorCollapse({
    displayMode,
    forceCollapsed,
    initialCollapsed: true,
    onPopoverToggle,
  });
  const [removingParticipantIds, setRemovingParticipantIds] = useState<Set<number>>(new Set());
  const numberOfParticipant = selectedParticipants.length;
  const minMaxAge = getMinMaxAges(consumerCategories);
  const totalPendingConsumerCategoryCount = Object.values(consumerCategoryCounts).reduce(
    (acc, val) => acc + val,
    0,
  );

  const isConsumerCategoryMode = participantMode === SELECTOR_MODE.CONSUMER_CATEGORY;
  const isHeaderClickable =
    !disabled && isConsumerCategoryMode && selectedParticipants.length > 0 && isCollapsed;

  useEffect(() => {
    setSelectedParticipants(participants ?? []);
    if (participantMode === SELECTOR_MODE.CONSUMER_CATEGORY && (participants?.length ?? 0) > 0) {
      setConsumerCategoryCounts(buildCountsFromParticipants(participants ?? []));
    }

    if (
      displayMode === DISPLAY_MODE.COLLAPSE &&
      participantMode === SELECTOR_MODE.CONSUMER_CATEGORY &&
      (!participants || participants.length === 0)
    ) {
      setIsCollapsed(false);
    } else {
      setIsCollapsed(true);
    }
  }, [participants, participantMode, displayMode, setIsCollapsed]);

  useEffect(() => {
    if (participantMode !== SELECTOR_MODE.CONSUMER_CATEGORY) return;
    if (!isCollapsed) return;

    setConsumerCategoryCounts(buildCountsFromParticipants(selectedParticipants));
  }, [isCollapsed, participantMode, selectedParticipants]);

  const handleAddParticipant = (age: string) => {
    const relatedCategory = consumerCategories.find(
      (category) =>
        category.ageMin <= Number.parseInt(age) &&
        category.ageMax >= Number.parseInt(age) &&
        category.student === false &&
        category.visible === true,
    );

    if (!relatedCategory) {
      console.error('No consumer category found for the given age:', age);
      return;
    }

    const newParticipant: Participant = {
      age: Number.parseInt(age),
      consumerCategory: relatedCategory,
      id: Math.max(...selectedParticipants.map((participant) => Number(participant.id)), 0) + 1,
      options: [],
    };
    const newParticipants = [...selectedParticipants, newParticipant];
    setSelectedParticipants(newParticipants);
    onChangeParticipants(newParticipants);
  };

  const handleConfirmParticipants = useCallback(() => {
    let lastId = Math.max(...selectedParticipants.map((p) => Number(p.id)), 0);
    const newParticipants: Participant[] = [];

    for (const consCat of consumerCategories) {
      const count = consumerCategoryCounts[consCat.shortname] ?? 0;
      if (count > 0) {
        const medianAge = Math.floor((consCat.ageMin + consCat.ageMax) / 2);
        for (let i = 0; i < count; i++) {
          lastId += 1;
          newParticipants.push({
            age: medianAge,
            consumerCategory: consCat,
            id: lastId,
            options: [],
          });
        }
      }
    }

    setSelectedParticipants(newParticipants);
    onChangeParticipants(newParticipants);
    setIsCollapsed(true);
  }, [
    consumerCategories,
    consumerCategoryCounts,
    onChangeParticipants,
    selectedParticipants,
    setIsCollapsed,
  ]);

  useEffect(() => {
    if (!onConsumerCategoryConfirmationChange) {
      return;
    }

    if (participantMode !== SELECTOR_MODE.CONSUMER_CATEGORY) {
      onConsumerCategoryConfirmationChange(null);
      return;
    }

    if (isCollapsed) {
      onConsumerCategoryConfirmationChange(null);
    } else {
      onConsumerCategoryConfirmationChange({
        confirm: handleConfirmParticipants,
        confirmDisabled: totalPendingConsumerCategoryCount === 0,
        isEditing: true,
      });
    }

    return () => {
      onConsumerCategoryConfirmationChange(null);
    };
  }, [
    handleConfirmParticipants,
    isCollapsed,
    onConsumerCategoryConfirmationChange,
    participantMode,
    totalPendingConsumerCategoryCount,
  ]);

  const incrementConsumerCategory = (shortname: string) => {
    setConsumerCategoryCounts((prev) => {
      const currentTotal = Object.values(prev).reduce((acc, value) => acc + value, 0);
      if (participantsLimit !== undefined && currentTotal >= participantsLimit) {
        return prev;
      }

      return {
        ...prev,
        [shortname]: (prev[shortname] ?? 0) + 1,
      };
    });
  };

  const decrementConsumerCategory = (shortname: string) => {
    setConsumerCategoryCounts((prev) => ({
      ...prev,
      [shortname]: Math.max(0, (prev[shortname] ?? 0) - 1),
    }));
  };

  const handleClickDeleteParticipant = (participantId: number) => {
    setRemovingParticipantIds((prev) => new Set(prev).add(participantId));

    globalThis.setTimeout(() => {
      const newParticipants = selectedParticipants.filter(
        (participant: Participant) => participant.id !== participantId,
      );
      setSelectedParticipants(newParticipants);
      onChangeParticipants(newParticipants);
      setRemovingParticipantIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(participantId);
        return newSet;
      });
    }, ANIMATION_PARTICIPANT_DURATION);
  };

  const showToggleButton = !disabled && isConsumerCategoryMode && selectedParticipants.length > 0;

  const selectorHeader = (
    <SelectorHeader
      isClosed={isCollapsed}
      isClickable={displayMode === DISPLAY_MODE.COLLAPSE && isHeaderClickable}
      onToggle={() => setIsCollapsed(!isCollapsed)}
      onHeaderClick={() => setIsCollapsed(false)}
      showToggleButton={displayMode === DISPLAY_MODE.COLLAPSE && showToggleButton}
    >
      <div className='ParticipantSelector_title'>
        <div
          className={`ParticipantSelector_participantsCountTitle ${
            disabled ? 'ParticipantSelector_participantsCountTitle-disabled' : ''
          }`}
          data-testid='ParticipantSelector_participantsCountTitle'
        >
          <FormattedMessage id='participants' defaultMessage='Participants' />
          {numberOfParticipant > 0 && (
            <div className='ParticipantSelector_count'>{numberOfParticipant}</div>
          )}
        </div>
        {participantsLimit === numberOfParticipant && (
          <div className='ParticipantSelector_subTitle'>
            <FormattedMessage
              id='participantsReachedLimit'
              defaultMessage='The maximum number of participants was reached.'
            />
          </div>
        )}
      </div>
    </SelectorHeader>
  );

  const participantContent = (
    <>
      {!disabled && isConsumerCategoryMode && (
        <Collapse isOpen={selectedParticipants.length === 0 || !isCollapsed}>
          <ParticipantConsumerCategorySelector
            key={`participant-selector-${isCollapsed ? 'closed' : 'open'}-${selectedParticipants.length}`}
            consumerCategories={consumerCategories}
            participantsLimit={participantsLimit}
            counts={consumerCategoryCounts}
            onIncrement={incrementConsumerCategory}
            onDecrement={decrementConsumerCategory}
          />
          {displayMode === DISPLAY_MODE.POPOVER && (
            <div className='ParticipantSelector_confirmAction'>
              <Button
                size={Size.S}
                color={ButtonColor.Primary}
                onClick={handleConfirmParticipants}
                disabled={totalPendingConsumerCategoryCount === 0}
              >
                <FormattedMessage id='confirm' defaultMessage='Confirm' />
              </Button>
            </div>
          )}
        </Collapse>
      )}

      {!disabled && (
        <Collapse isOpen={isCollapsed}>
          <div className='ParticipantSelector_participantsContainer'>
            {selectedParticipants.map((participant, index) => {
              const currentOrderItem = orderItems.find(
                (orderItem) => orderItem.participantId === participant.id,
              );
              const price = currentOrderItem
                ? getTotalPriceWithOptions(currentOrderItem)
                : undefined;
              const isRemoving = removingParticipantIds.has(participant.id);

              return (
                <Collapse key={participant.id} isOpen={!isRemoving}>
                  <ParticipantLine
                    participant={participant}
                    price={price}
                    currency={currency}
                    index={index}
                    onDeleteClick={handleClickDeleteParticipant}
                    isOnError={
                      isSimulationOnError && (price === undefined || currency === undefined)
                    }
                    participantMode={participantMode}
                    isLoading={isLoading}
                    allParticipants={selectedParticipants}
                  />
                </Collapse>
              );
            })}
            {participantMode === SELECTOR_MODE.AGE && (
              <Button
                size={Size.S}
                onClick={() => setIsOpenModalParticipant(true)}
                aria-label={intl.formatMessage(
                  {
                    defaultMessage: `Add information for participant number {numberParticipant}`,
                    id: 'addParticipantInfo',
                  },
                  { numberParticipant: numberOfParticipant + 1 },
                )}
                disabled={participantsLimit === numberOfParticipant}
              >
                <FormattedMessage defaultMessage='Add participant' id='addParticipant' />
              </Button>
            )}
          </div>
        </Collapse>
      )}
    </>
  );

  return (
    <div className='ParticipantSelector'>
      {displayMode === DISPLAY_MODE.POPOVER ? (
        <SelectorLayout
          displayMode={DISPLAY_MODE.POPOVER}
          isOpen={disabled ? false : !isCollapsed}
          onToggle={toggle}
          onClickOutside={onClickOutside}
          header={selectorHeader}
          popoverWidth='fit-content'
        >
          {participantContent}
        </SelectorLayout>
      ) : (
        <>
          {selectorHeader}
          {participantContent}
        </>
      )}

      {participantMode === SELECTOR_MODE.AGE && isOpenModalParticipant && (
        <ParticipantAgeModal
          open
          onClose={handleCloseModalParticipant}
          onSubmit={handleAddParticipant}
          minMaxAge={minMaxAge}
        />
      )}
    </div>
  );
};

export default ParticipantSelector;
